import { useState, type HTMLAttributes } from 'react';

import { cn } from '@/shared/lib/cn';

const sizeMap = {
  sm: 'h-8 w-8 text-label-md',
  md: 'h-10 w-10 text-label-lg',
  lg: 'h-14 w-14 text-display-sm',
} as const;

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  name: string;
  size?: keyof typeof sizeMap;
  testID?: string;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === '') return '?';
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

export function Avatar({ src, name, size = 'md', className, testID, ...props }: AvatarProps) {
  const [errored, setErrored] = useState(false);
  const showImage = !!src && !errored;
  return (
    <div
      data-testid={testID}
      role="img"
      aria-label={name}
      className={cn(
        'inline-flex items-center justify-center overflow-hidden rounded-pill bg-surface-high font-semibold text-on-surface',
        sizeMap[size],
        className,
      )}
      {...props}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          aria-hidden
          className="h-full w-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        <span aria-hidden>{initialsOf(name)}</span>
      )}
    </div>
  );
}
